import React, { ReactNode, useMemo } from 'react';
import '../AlertManagement.scss';
import { ApiNote } from '../../../api';
import { formatDate, formatTime } from '../../../common/datetime/FormattedDateTime';
import {
  InternalApiAlertStatusEvent,
  InternalApiNoteDraft,
  InternalApiScreeningAlertStatus,
} from '../../../api-internal';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ContentPreviewer } from '../../../common/ContentPreviewer';
import { useAlertStatuses } from '../../../screening/alert-status/alertStatusesHelper';
import Tracker from '../../../tracking/tracker';
import { Card } from 'react-bootstrap';
import { SalvTextField, Size } from '@salvapp/ui-components';
import { NoteDraftItem } from './NoteDraftItem';

interface Props {
  notes: ApiNote[];
  statusChanges: InternalApiAlertStatusEvent[];
  drafts?: InternalApiNoteDraft[];
  onEditDraft?: (draft: InternalApiNoteDraft) => void;
  onDeleteDraft?: (draft: InternalApiNoteDraft) => void;
  isDraftActiveElsewhere?: (draftId: number) => boolean;
  children?: ReactNode;
  cardAction?: ReactNode;
}

export const AlertNotes = ({
  notes,
  statusChanges,
  drafts,
  onEditDraft,
  onDeleteDraft,
  isDraftActiveElsewhere,
  children,
  cardAction,
}: Props) => {
  const [showingNotes, setShowingNotes] = React.useState(5);
  const [searchQuery, setSearchQuery] = React.useState('');
  const notesToShow = useMemo(() => {
    if (!searchQuery) {
      return notes;
    }
    return notes.filter((n) => n.content?.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [notes, searchQuery]);
  const hasMoreNotesToShow = showingNotes < notesToShow.length;

  const [, allStatuses, , refreshStatuses] = useAlertStatuses();
  const statusChangesById = useMemo(() => {
    if (!allStatuses) return new Map<number, [InternalApiScreeningAlertStatus, InternalApiScreeningAlertStatus]>();
    const orgStatusesById = new Map(allStatuses.map((s) => [s.id!, s]));
    return calculateStatusChangesById(statusChanges || [], orgStatusesById, refreshStatuses);
  }, [statusChanges, allStatuses, refreshStatuses]);

  return (
    <Card className='alert-management-notes'>
      <div className='p-x-24 d-flex align-items-center'>
        <span className='title-s p-y-16'>Notes</span>
        <div className='spacer' />
        {notes.length > 0 && (
          <SalvTextField
            ariaLabel='Search notes'
            className={'p-r-8'}
            size={Size.SMALL}
            placeholder='Search submitted notes...'
            onChange={setSearchQuery}
            value={searchQuery}
          />
        )}
        {cardAction}
      </div>
      {children}
      {drafts &&
        drafts.length > 0 &&
        drafts.map((draft) => (
          <NoteDraftItem
            key={`draft-${draft.id}`}
            draft={draft}
            isActiveElsewhere={isDraftActiveElsewhere?.(draft.id)}
            onEdit={onEditDraft ?? (() => {})}
            onDelete={onDeleteDraft ?? (() => {})}
          />
        ))}
      {notesToShow.length < 1 && !children && (!drafts || drafts.length === 0) && (
        <div className={'d-flex divider'}>
          <div className='spacer' />
          <span className='text-s p-y-16 nothing-to-show'>Nothing to show here, yet.</span>
          <div className='spacer' />
        </div>
      )}
      {notesToShow?.slice(0, showingNotes).map((note) => (
        <div className='divider p-x-24 p-y-16' key={note.id}>
          <div className='p-b-4 note-title'>
            <span className='text-strong-s'>{note.user?.name}</span>
            {note.createdTime && (
              <>
                <span className='text-strong-s no-break'>, {formatDate(note.createdTime)}</span>
                <span className='text-strong-s p-l-4 p-r-8 no-break'>{formatTime(note.createdTime)}</span>
              </>
            )}
            <div className='spacer' />
            {note.statusChangeId && noteRelatedStatusChange(statusChangesById, note)}
          </div>
          <ContentPreviewer previewLinesAmount={3}>{note.content}</ContentPreviewer>
        </div>
      ))}
      {hasMoreNotesToShow && (
        <div className='divider p-y-8'>
          <div className='note-show-more' onClick={() => setShowingNotes(showingNotes * 2)}>
            <span className='text-strong-s'>Show older</span>
          </div>
        </div>
      )}
    </Card>
  );
};

const noteRelatedStatusChange = (
  statusChanges: Map<number, [InternalApiScreeningAlertStatus, InternalApiScreeningAlertStatus]>,
  note: ApiNote
) => {
  const [from, to] = statusChanges.get(note.statusChangeId || 0) || [];
  if (!from || !to) {
    return;
  }
  return (
    <span className='text-strong-xs status-change'>
      <span className='old-status'>{from.textName}</span>
      <FontAwesomeIcon className='p-x-4' icon={faArrowRight} />
      {to.textName}
    </span>
  );
};

const calculateStatusChangesById = (
  statusesHistory: InternalApiAlertStatusEvent[],
  orgStatuses: Map<number, InternalApiScreeningAlertStatus>,
  refreshStatuses: () => void
) => {
  const newMap = new Map<number, [InternalApiScreeningAlertStatus, InternalApiScreeningAlertStatus]>();
  let shouldRefresh = false;
  for (let i = 0; i < statusesHistory.length - 1; i++) {
    const statusChangeTo = statusesHistory[i];
    const statusChangeFrom = statusesHistory[i + 1];
    const newStatus = orgStatuses.get(statusChangeTo.statusId!);
    const oldStatus = orgStatuses.get(statusChangeFrom.statusId!);
    if (newStatus && oldStatus) {
      newMap.set(statusChangeTo.id!, [oldStatus, newStatus]);
    } else if (!newStatus) {
      console.warn(
        `Status with id ${statusChangeTo.statusId} not found. You might need to refresh the page, as the status was recently added or changed by admin`
      );
      shouldRefresh = true;
    } else {
      Tracker.error(new Error(`Status with id ${statusChangeFrom.statusId} not found`));
    }
  }
  if (shouldRefresh) {
    refreshStatuses();
  }
  return newMap;
};
